import type { SplitView } from "@/lib/activity-sessions/split-service";
import { formatMinorCurrency } from "@/lib/utils";

/**
 * The wording for the two pricing types.
 *
 * Splitting a cost and charging a fixed price look like two ways of writing a
 * number. They are two different products, and the same two fields mean
 * opposite things in each — so the mechanics never fork, but the copy always
 * does. Everything user-facing about a split routes through here so the two
 * voices cannot drift apart.
 */

export const inr = (minor: number) => formatMinorCurrency(minor, "INR");

export const isSplitting = (view: { pricing: SplitView["pricing"] }): boolean =>
  view.pricing === "divide_total";

/** What the joiner sees before they commit. */
export function joinerCopy(
  view: SplitView,
  maxParticipants: number,
): {
  headline: string;
  detail: string;
} {
  const fixed = !isSplitting(view);
  const credit = view.mode === "hiive_money";
  const extras = view.extrasAllowed && view.extrasAllowanceMinor > 0;
  const note = view.extrasNote?.trim();

  if (fixed) {
    if (!credit) {
      return {
        headline: `${inr(view.perHeadMinor)} per person`,
        detail: extras
          ? `A fixed price. The organizer may add up to ${inr(view.extrasAllowanceMinor)} of extras after${note ? ` — ${note}` : ""}. Settle up with them after.`
          : "A fixed price. Settle up with the organizer after.",
      };
    }
    return extras
      ? {
          headline: `${inr(view.perHeadMinor)} · ${inr(view.blockedPerShareMinor)} blocked`,
          detail: extrasBlockDetail(view, note),
        }
      : {
          headline: `${inr(view.worstCaseMinor)} · blocked from your wallet`,
          // Someone who has seen the split version will otherwise assume it drops.
          detail:
            "A fixed price — it won't change if more people join. Charged after the session, released if it doesn't happen.",
        };
  }

  const best = view.totalAmountMinor
    ? Math.ceil(view.totalAmountMinor / Math.max(1, maxParticipants))
    : view.perHeadMinor;

  if (!credit) {
    return {
      headline: `Your share: ${inr(view.perHeadMinor)}`,
      detail: extras
        ? `Drops as more people join. The organizer may add up to ${inr(view.extrasAllowanceMinor)} of extras after${note ? ` — ${note}` : ""}.`
        : "Drops as more people join. Settle up after the game.",
    };
  }

  // The blocked figure is now BIGGER than the share, so lead with why rather
  // than letting somebody discover a number they didn't expect.
  return extras
    ? {
        headline: `Your share: ${inr(best)}–${inr(view.worstCaseMinor)} · ${inr(view.blockedPerShareMinor)} blocked`,
        detail: extrasBlockDetail(view, note),
      }
    : {
        headline: `Your share: ${inr(best)}–${inr(view.worstCaseMinor)} · ${inr(view.worstCaseMinor)} blocked`,
        detail:
          "We block the most it could ever be. After the session you're charged what it actually came to and the rest is released — and if the session doesn't happen, all of it is.",
      };
}

/**
 * Why the blocked number is bigger than the share. Three facts in order: the
 * ceiling on the share, the extra headroom and what it's for, and that the
 * organizer cannot exceed it.
 */
function extrasBlockDetail(view: SplitView, note: string | undefined): string {
  const cap = isSplitting(view)
    ? `${inr(view.worstCaseMinor)} is the most your share can be.`
    : `${inr(view.worstCaseMinor)} is the price and it won't change.`;
  return `${cap} We block up to ${inr(view.extrasAllowanceMinor)} on top because the organizer may add extras${
    note ? ` — ${note}` : ""
  }. They can't add more than that. Whatever isn't used comes straight back after the game.`;
}

/** The extras ceiling, stated as the organizer set it up. */
export function extrasPolicyCopy(input: {
  extrasAllowed: boolean;
  allowanceMinor: number;
  note: string | null;
}): string | null {
  if (!input.extrasAllowed) return null;
  return `Extras up to ${inr(input.allowanceMinor)} each${
    input.note?.trim() ? ` — ${input.note.trim()}` : ""
  }`;
}

/** The organizer's live preview at the current headcount. */
export function organizerSummary(view: SplitView): string {
  if (view.status === "closed") {
    const amounts = view.shares
      .filter(
        (share) =>
          share.isRosterMember && (!share.isOrganizer || view.organizerPlaying),
      )
      .map((share) => share.amountMinor ?? share.chargeableMinor)
      .filter((amount) => amount > 0);
    const finalTotal = amounts.reduce((sum, amount) => sum + amount, 0);
    const low = amounts.length > 0 ? Math.min(...amounts) : view.perHeadMinor;
    const high = amounts.length > 0 ? Math.max(...amounts) : view.perHeadMinor;
    const perPlayer =
      low === high
        ? `${inr(high)} each`
        : `${inr(low)}–${inr(high)} per player`;
    const baseTotal =
      view.pricing === "divide_total"
        ? (view.totalAmountMinor ?? 0)
        : (view.perPersonAmountMinor ?? 0) * amounts.length;
    const extrasTotal = Math.max(0, finalTotal - baseTotal);
    return `${inr(finalTotal)} final · ${perPlayer}${
      extrasTotal > 0 ? ` · includes ${inr(extrasTotal)} extras` : ""
    }`;
  }

  const totalMinor = view.totalAmountMinor ?? 0;
  const belowMinimum =
    isSplitting(view) && view.perHeadMinor * view.payingCount < totalMinor;
  return isSplitting(view)
    ? belowMinimum
      ? `${inr(totalMinor)} total · ${inr(view.perHeadMinor)} maximum until enough players join`
      : `${inr(totalMinor)} split ${view.payingCount} ${view.payingCount === 1 ? "way" : "ways"} · ${inr(view.perHeadMinor)} each`
    : view.payingCount === 0
      ? `${inr(view.perPersonAmountMinor ?? 0)} each · no players yet`
      : `${inr(view.perPersonAmountMinor ?? 0)} each · ${inr(view.organizerTakeMinor)} at ${view.payingCount} ${view.payingCount === 1 ? "person" : "people"}`;
}

/**
 * Sharing works in opposite directions. Splitting a cost, "bring someone and
 * everyone pays less" is a real recruiting loop. At a fixed price that is a lie
 * — nobody's price moves — so it becomes spots remaining, aimed at the organizer.
 */
export function shareNudge(
  view: SplitView,
  maxParticipants: number,
): string | null {
  if (view.status !== "open") return null;
  if (!isSplitting(view)) {
    const left = Math.max(0, maxParticipants - view.payingCount);
    return left > 0
      ? `${left} spot${left === 1 ? "" : "s"} left — every one is another ${inr(view.perPersonAmountMinor ?? 0)}`
      : null;
  }
  if (!view.totalAmountMinor || view.payingCount >= maxParticipants)
    return null;
  const next = Math.ceil(view.totalAmountMinor / (view.payingCount + 1));
  return next < view.perHeadMinor
    ? `One more player and everyone pays ${inr(next)}`
    : null;
}

export const STATUS_LABEL: Record<string, string> = {
  open: "Owes",
  blocked: "Blocked",
  awaiting_confirmation: "Says they paid",
  settled: "Settled",
  waived: "Waived",
  disputed: "Flagged",
};

export const SETTLED_VIA_LABEL: Record<string, string> = {
  hiive_money: "Hiive Money",
  offline: "Paid directly",
  mixed: "Hiive Money + paid directly",
  waived: "Waived",
  organizer: "Organizer",
};

/**
 * How the payer says they sent it. Descriptive — we implement none of these,
 * and the payment instructions themselves are deliberately rail-agnostic.
 */
export const PAYMENT_METHOD_LABEL: Record<string, string> = {
  upi: "UPI",
  cash: "Cash",
  bank: "Bank transfer",
  other: "Something else",
};

export const DISPUTE_REASON_LABEL: Record<string, string> = {
  didnt_play: "Didn't play",
  wrong_amount: "Wrong amount",
  already_paid: "Already paid",
  other: "Something else",
};
